<script setup lang="ts">
import '@videojs/html/audio/player';
import '@videojs/html/audio/compat-skin';

const skin = ref<HTMLElement>();

// audio-compat-skin renders its controls into an open shadow root, so hidden
// buttons must be removed there directly; page-level CSS can't reach them.
function removeControl(shadowRoot: ShadowRoot, selector: string) {
  const control = shadowRoot.querySelector(selector);
  if (!control) return;

  const triggerId = control.id;
  if (triggerId) {
    shadowRoot.querySelector(`media-tooltip[trigger="${triggerId}"]`)?.remove();
  }

  control.remove();
}

onMounted(async () => {
  // The element may still be un-upgraded (its defining chunk hasn't run yet),
  // in which case it has no shadow root until the browser upgrades it.
  await customElements.whenDefined('audio-compat-skin');

  const shadowRoot = skin.value?.shadowRoot;
  if (!shadowRoot) return;

  removeControl(shadowRoot, '.media-captions-button');
  removeControl(shadowRoot, '.media-airplay-button');

  // The skin's own stylesheet hides seek buttons on mobile via a
  // :where() selector (zero specificity), so a plain rule injected
  // into the shadow root overrides it without needing !important.
  const style = document.createElement('style');
  style.textContent = '.media-seek-button-audio { display: inline-flex; }';
  shadowRoot.appendChild(style);
});
</script>

<template>
  <!--
    The player element owns and shares state between the UI
    components and Media. Put layout on the skin or container.
   -->
  <audio-player>
    <!--
      Skins contain the entire player UI and are easily swappable.
      Add the skin source to your project for full control over its
      UI components.
     -->
    <audio-compat-skin ref="skin">
      <!--
          Media are players without UIs, handling networking
          and display of the media. They are easily swappable
          to handle different sources.
        -->
      <slot />
    </audio-compat-skin>
  </audio-player>
</template>
